import { lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import BootcampTokenizacion from "@/pages/BootcampTokenizacion";
import EventosListing from "@/pages/EventosListing";
import EventoDetail from "@/pages/EventoDetail";
import ComoFunciona from "@/pages/ComoFunciona";
import FreedomHomesE from "@/pages/FreedomHomesE";
import Presentacion from "@/pages/Presentacion";
import MasterclassReplay from "@/pages/MasterclassReplay";
import MasterclassEnDirecto from "@/pages/MasterclassEnDirecto";
import TerminosCondiciones from "@/pages/fhc/TerminosCondiciones";
import PoliticaPrivacidadFHC from "@/pages/fhc/PoliticaPrivacidadFHC";
import MembresiasSelector from "@/pages/membresias/MembresiasSelector";
import MembresiaDetalle from "@/pages/membresias/MembresiaDetalle";
import { DashboardProvider } from "@/context/DashboardContext";
import { CopyProvider } from "@/context/CopyContext";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import ProtectedDashboard from "@/components/dashboard/ProtectedDashboard";
import DashboardLogin from "@/pages/dashboard/DashboardLogin";
import DashboardHome from "@/pages/dashboard/DashboardHome";
import DashboardActividad from "@/pages/dashboard/DashboardActividad";
import DashboardPropiedades from "@/pages/dashboard/DashboardPropiedades";
import DashboardAcademia from "@/pages/dashboard/DashboardAcademia";
import DashboardCurso from "@/pages/dashboard/DashboardCurso";
import DashboardDiploma from "@/pages/dashboard/DashboardDiploma";
import DiplomaVerify from "@/pages/academy/DiplomaVerify";
import DashboardSuccessKit from "@/pages/dashboard/DashboardSuccessKit";
import DashboardMarketing from "@/pages/dashboard/DashboardMarketing";
import DashboardAfiliados from "@/pages/dashboard/DashboardAfiliados";
import DashboardComisiones from "@/pages/dashboard/DashboardComisiones";
import DashboardPerfil from "@/pages/dashboard/DashboardPerfil";
import PremiumOnly, { AccessGate } from "@/components/dashboard/PremiumOnly";
import MembersOnly from "@/components/dashboard/MembersOnly";
import { DashboardDocumentos, DashboardDocumentoDetalle } from "@/pages/dashboard/DashboardDocumentos";
// Calculadora: solo Premium. Se carga aparte (lazy) para que su código no viaje en el bundle general.
const DashboardCalculadora = lazy(() => import("@/pages/dashboard/DashboardCalculadora"));
import DashboardAdmin from "@/pages/dashboard/DashboardAdmin";
import DashboardPropertyDetail from "@/pages/dashboard/DashboardPropertyDetail";
import DashboardRegister from "@/pages/dashboard/DashboardRegister";
import DashboardEventos from "@/pages/dashboard/DashboardEventos";
import DashboardForgotPassword from "@/pages/dashboard/DashboardForgotPassword";
import DashboardResetPassword from "@/pages/dashboard/DashboardResetPassword";
import DashboardMisOperaciones from "@/pages/dashboard/DashboardMisOperaciones";
import DashboardChat from "@/pages/dashboard/DashboardChat";
import PublicChatWidget from "@/components/landing/PublicChatWidget";
import Contacto from "@/pages/Contacto";
import NotFound from "@/pages/NotFound";
import ErrorBoundary from "@/components/common/ErrorBoundary";
import ThankYou from "@/pages/ThankYou";
import PublicLayout from "@/components/site/PublicLayout";
import { SiteLangProvider } from "@/components/site/SiteLang";
// Import early so the IIFE captures ?ref= before any navigation
import "@/hooks/useReferral";

// El chat público es para la web: dentro del área de miembros ya está el Asistente IA
function PublicChat() {
  const { pathname } = useLocation();
  return pathname.startsWith('/dashboard') ? null : <PublicChatWidget />;
}

function App() {
  return (
    <div className="App">
        <DashboardProvider>
        <CopyProvider>
        <BrowserRouter>
          <SiteLangProvider>
          <ErrorBoundary>
          <Routes>
            {/* Web pública: cabecera y pie fijos, comunes a todas las páginas (09/10/2026) */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<FreedomHomesE />} />
              <Route path="/bootcamp-tokenizacion" element={<BootcampTokenizacion />} />
              <Route path="/eventos" element={<EventosListing />} />
              <Route path="/eventos/:slug" element={<EventoDetail />} />
              <Route path="/como-funciona" element={<ComoFunciona />} />
              <Route path="/contacto" element={<Contacto />} />
              <Route path="/diploma/:code" element={<DiplomaVerify />} />
              <Route path="/thank-you" element={<ThankYou />} />
              <Route path="/terms-conditions" element={<TerminosCondiciones />} />
              <Route path="/privacy-policy-fhc" element={<PoliticaPrivacidadFHC />} />
              <Route path="/membresias" element={<MembresiasSelector />} />
              <Route path="/membresias/pro" element={<MembresiaDetalle type="basica" />} />
              <Route path="/membresias/premium" element={<MembresiaDetalle type="premium" />} />
              <Route path="*" element={<NotFound />} />
            </Route>
            {/* Landings heredadas (taller/certificación antigua) → páginas vigentes */}
            <Route path="/broker" element={<Navigate to="/membresias/premium" replace />} />
            <Route path="/taller-marzo-alicante" element={<Navigate to="/eventos" replace />} />
            <Route path="/membresia-freedom" element={<Navigate to="/membresias" replace />} />
            <Route path="/bootcamps" element={<Navigate to="/eventos" replace />} />
            {/* Antigua landing de un evento ya celebrado (seguía aceptando pagos) → eventos vigentes */}
            <Route path="/eventos-legacy" element={<Navigate to="/eventos" replace />} />
            <Route path="/holding-tokenizado" element={<Navigate to="/como-funciona" replace />} />
            <Route path="/test-de-idoneidad" element={<Navigate to="/como-funciona" replace />} />
            <Route path="/presentacion" element={<Presentacion />} />
            {/* Replay de la masterclass (22/10/2026): vídeo + agendar llamada con el equipo. Por enlace directo (noindex) */}
            <Route path="/masterclass" element={<MasterclassReplay />} />
            {/* Registro a la próxima masterclass online (02/11/2026): solo nombre y email; enlace de afiliado «Masterclass» */}
            <Route path="/masterclass-en-directo" element={<MasterclassEnDirecto />} />
            <Route path="/login" element={<Navigate to="/dashboard/login" replace />} />
            <Route path="/politica-privacidad" element={<Navigate to="/privacy-policy-fhc" replace />} />
            <Route path="/admin" element={<Navigate to="/dashboard/admin" replace />} />
            <Route path="/admin/bootcamp" element={<Navigate to="/dashboard/admin?tab=eventos&sub=bootcamps" replace />} />
            {/* Redirects from old routes */}
            <Route path="/freedom-homes-club" element={<Navigate to="/" replace />} />
            <Route path="/presentacion-freedom" element={<Navigate to="/" replace />} />
            <Route path="/unidos-global" element={<Navigate to="/" replace />} />
            <Route path="/bootcamp-resultados" element={<Navigate to="/bootcamp-tokenizacion" replace />} />
            {/* Antiguo mini-site FHC (retirado 08/10/2026) → páginas vigentes */}
            <Route path="/el-circulo" element={<Navigate to="/membresias" replace />} />
            <Route path="/the-framework" element={<Navigate to="/como-funciona" replace />} />
            <Route path="/access-levels" element={<Navigate to="/membresias" replace />} />
            <Route path="/the-immersive" element={<Navigate to="/bootcamp-tokenizacion" replace />} />
            <Route path="/qualification" element={<Navigate to="/membresias" replace />} />
            <Route path="/legal-notice" element={<Navigate to="/terms-conditions" replace />} />
            <Route path="/membership-agreement" element={<Navigate to="/terms-conditions" replace />} />
            {/* Landing Ads CRO Page */}
            {/* Landing de anuncios antigua (contador de plazas y testimonios no verificables) → selector de membresías */}
            <Route path="/landing-ads" element={<Navigate to="/membresias" replace />} />
            <Route path="/membresias/basica" element={<Navigate to="/membresias/pro" replace />} />
            <Route path="/membresias-pdf" element={<Navigate to="/membresias" replace />} />
            {/* Broker Success Kit */}
            {/* El kit del broker contiene el plan de compensación: solo dentro del dashboard (requiere login) */}
            <Route path="/broker-success-kit" element={<Navigate to="/dashboard/success-kit" replace />} />
            {/* Confirmación Bootcamp — standalone, no navbar */}
            <Route path="/confirmacion-bootcamp" element={<Navigate to="/dashboard/eventos" replace />} />
            {/* Broker Dashboard */}
            <Route path="/dashboard/login" element={<DashboardLogin />} />
            <Route path="/dashboard/register" element={<DashboardRegister />} />
            <Route path="/dashboard/forgot-password" element={<DashboardForgotPassword />} />
            <Route path="/dashboard/reset-password" element={<DashboardResetPassword />} />
            <Route path="/dashboard" element={<ProtectedDashboard><DashboardLayout /></ProtectedDashboard>}>
              <Route index element={<DashboardHome />} />
              {/* Niveles de acceso (09/10/2026): la matriz vive en components/dashboard/access.js; el servidor aplica la misma */}
              <Route path="actividad" element={<AccessGate feature="sales" section="Actividad comercial"><DashboardActividad /></AccessGate>} />
              <Route path="tokens" element={<Navigate to="/dashboard/actividad" replace />} />
              {/* 16/10/2026: «Rendimiento» pasa a ser «Mis operaciones» (todos los socios) y su resumen de comisiones vive en Comisiones */}
              <Route path="operaciones" element={<AccessGate feature="operations" section="Mis operaciones"><DashboardMisOperaciones /></AccessGate>} />
              <Route path="rendimiento" element={<Navigate to="/dashboard/operaciones" replace />} />
              <Route path="propiedades" element={<MembersOnly><DashboardPropiedades /></MembersOnly>} />
              <Route path="propiedades/:id" element={<MembersOnly><DashboardPropertyDetail /></MembersOnly>} />
              <Route path="academia" element={<DashboardAcademia />} />
              <Route path="academia/diploma/:code" element={<DashboardDiploma />} />
              <Route path="academia/:courseId" element={<DashboardCurso />} />
              <Route path="success-kit" element={<AccessGate feature="successKit" section="Success Kit"><DashboardSuccessKit /></AccessGate>} />
              <Route path="marketing" element={<AccessGate feature="marketing" section="Marketing"><DashboardMarketing /></AccessGate>} />
              <Route path="afiliados" element={<AccessGate feature="network" section="Red de afiliados"><DashboardAfiliados /></AccessGate>} />
              <Route path="mis-contactos" element={<Navigate to="/dashboard/comisiones?tab=pipeline" replace />} />
              <Route path="comisiones" element={<AccessGate feature="commissions" section="Comisiones"><DashboardComisiones /></AccessGate>} />
              <Route path="chat" element={<AccessGate feature="chat" section="Asistente IA"><DashboardChat /></AccessGate>} />
              <Route path="calculadora" element={<PremiumOnly feature="calculator" section="Calculadora"><Suspense fallback={null}><DashboardCalculadora /></Suspense></PremiumOnly>} />
              <Route path="eventos" element={<DashboardEventos />} />
              <Route path="documentos" element={<AccessGate feature="documents" section="Mis documentos"><DashboardDocumentos /></AccessGate>} />
              <Route path="documentos/:id" element={<AccessGate feature="documents" section="Mis documentos"><DashboardDocumentoDetalle /></AccessGate>} />
              <Route path="perfil" element={<DashboardPerfil />} />
              <Route path="admin" element={<ProtectedDashboard adminOnly><DashboardAdmin /></ProtectedDashboard>} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
          </ErrorBoundary>
          <PublicChat />
          </SiteLangProvider>
        </BrowserRouter>
        </CopyProvider>
        </DashboardProvider>
        <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
